/**
 * Browser side of invoice payment.
 *
 * The server creates the Airwallex payment intent and hands back its id, its
 * client secret and which Airwallex environment it lives in, so the page can
 * never point at a different environment from the one the intent was made in.
 * The SDK itself is imported only here, and only when a payment starts, so the
 * rest of the site never downloads it.
 */

const ENDPOINT = '/api/pay/intent';

export class PaymentError extends Error {
  constructor(code, { status = 0, field = null } = {}) {
    super(code);
    this.name = 'PaymentError';
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

export async function createIntent(payload) {
  let response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(20000),
    });
  } catch {
    throw new PaymentError('network');
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new PaymentError(body.error || 'server', { status: response.status, field: body.field || null });
  return body;
}

/** Airwallex's payment-form locales for the site's four languages. */
const AIRWALLEX_LOCALE = { en: 'en', 'zh-Hant': 'zh-HK', 'zh-Hans': 'zh', ja: 'ja' };

let sdk = null;
let initialisedFor = null;

/**
 * Mount the card form for an intent. 3-D Secure renders into `authContainer`,
 * on this page — the checkout never redirects to a hosted payment page, which
 * is what the card schemes' "no redirection" requirement asks for.
 */
/**
 * Tell the server why the card form failed. A payment that fails in a payer's
 * browser is otherwise invisible to us — the page shows a generic message and
 * the reason goes nowhere. Best-effort: a failure to report is ignored.
 */
function reportError(intent, detail) {
  const error = detail?.error || detail || {};
  try {
    navigator.sendBeacon?.(
      '/api/pay/client-error',
      new Blob([JSON.stringify({ intentId: intent?.intentId, code: error.code, message: error.message })], { type: 'application/json' }),
    );
  } catch {
    /* reporting is a courtesy; never let it break the page */
  }
}

export async function mountCardForm({ intent, lang, container, authContainer, onSuccess, onError }) {
  sdk ||= await import('@airwallex/components-sdk');

  const key = `${intent.env}:${lang}`;
  if (initialisedFor !== key) {
    await sdk.init({ env: intent.env, enabledElements: ['payments'], locale: AIRWALLEX_LOCALE[lang] || 'en' });
    initialisedFor = key;
  }

  const element = await sdk.createElement('dropIn', {
    intent_id: intent.intentId,
    client_secret: intent.clientSecret,
    currency: intent.currency,
    methods: ['card'],
    authFormContainer: authContainer,
  });
  element.mount(container);
  // Whether the payer has pressed Pay. An error before that is about the form
  // or the account, never the payer's card, and must not be reported as one.
  let attempted = false;
  element.on('clickConfirmButton', () => {
    attempted = true;
  });
  element.on('success', (event) => onSuccess(event?.detail));
  element.on('error', (event) => {
    // Airwallex's own reason, which the payer never sees: keep it visible to us.
    console.warn('[airwallex] drop-in error', event?.detail);
    reportError(intent, event?.detail);
    onError(event?.detail, { attempted });
  });
  return element;
}
